import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "success" | "outline" | "soft";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white hover:bg-primary-600 shadow-[0_6px_18px_-8px_rgb(47_107_255/0.8)] disabled:bg-primary/50",
  secondary: "bg-panel-2 text-ink border border-line-strong hover:bg-panel-3 hover:border-[#31446b]",
  ghost: "text-ink-2 hover:text-ink hover:bg-panel-2",
  danger: "bg-danger-600 text-white hover:bg-danger",
  success: "bg-success-600 text-white hover:bg-success",
  outline: "border border-primary/60 text-primary-300 hover:bg-primary/10 hover:text-white",
  soft: "bg-primary/15 text-primary-300 hover:bg-primary/25",
};

const sizes: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-xs gap-1.5 rounded-lg",
  sm: "h-8 px-3 text-[13px] gap-1.5 rounded-lg",
  md: "h-9.5 px-4 text-[13.5px] gap-2 rounded-[10px]",
  lg: "h-11 px-5 text-sm gap-2 rounded-xl",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, leftIcon, rightIcon, block, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      data-variant={variant}
      className={cn(
        "inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60",
        variants[variant],
        sizes[size],
        block && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: "ghost" | "secondary" | "primary" | "danger";
  size?: "sm" | "md" | "lg";
  badge?: number | null;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = "ghost", size = "md", badge, className, children, type = "button", ...rest },
  ref,
) {
  const dims = size === "sm" ? "size-8 rounded-lg" : size === "lg" ? "size-11 rounded-xl" : "size-9.5 rounded-[10px]";
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center transition-colors disabled:opacity-50",
        dims,
        variant === "ghost" && "text-ink-2 hover:bg-panel-2 hover:text-ink",
        variant === "secondary" && "border border-line-strong bg-panel-2 text-ink-2 hover:bg-panel-3 hover:text-ink",
        variant === "primary" && "bg-primary text-white hover:bg-primary-600",
        variant === "danger" && "text-danger hover:bg-danger/10",
        className,
      )}
      {...rest}
    >
      {children}
      {badge ? (
        <span className="absolute -right-1 -top-1 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-canvas">
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </button>
  );
});

/** A react-router link styled as a button. */
export function ButtonLink({
  variant = "primary",
  size = "md",
  className,
  leftIcon,
  rightIcon,
  children,
  ...rest
}: LinkProps & { variant?: ButtonVariant; size?: ButtonSize; leftIcon?: ReactNode; rightIcon?: ReactNode }) {
  return (
    <Link
      data-variant={variant}
      className={cn(
        "inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-colors duration-150",
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </Link>
  );
}
